import { type ChangeEvent, type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import {
  useGenerateSpec,
  useQuestionnaire,
  useSaveAnswer,
  useSetSourceDocument,
} from '../../api/projects.js';
import { Alert, Button, buttonClass, Card, Spinner, TextArea } from '../../components/ui.js';
import { useProjectContext } from './ProjectShell.js';

export function QuestionnairePage() {
  const { project } = useProjectContext();
  const { data, isPending, isError } = useQuestionnaire(project.id);
  const save = useSaveAnswer(project.id);
  const generate = useGenerateSpec(project.id);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const status = project.status;
  // A draft already exists once generation has run (and until the founder
  // approves it). In those states the answers stay editable so a change can be
  // fed back into a fresh specification. A generation in flight is momentarily
  // read-only; once approved and building, the answers are locked for good.
  const generating = status === 'generating_spec';
  const hasSpec = status === 'spec_review' || status === 'spec_shared';
  const editable = status === 'questionnaire' || hasSpec;
  const locked = !editable && !generating;

  if (isPending) return <Card>{<Spinner />}</Card>;
  if (isError || !data) return <Card>{<Alert>Could not load the questionnaire.</Alert>}</Card>;

  const answers = data.answers;
  const valueOf = (key: string): string => draft[key] ?? answers[key] ?? '';
  const dirty = data.questions.some(
    (q) => draft[q.key] !== undefined && draft[q.key] !== answers[q.key],
  );
  const requiredComplete = data.questions
    .filter((q) => q.required)
    .every((q) => valueOf(q.key).trim().length > 0);
  // A source document can stand in for the questionnaire.
  const canContinue = (requiredComplete || Boolean(project.sourceDocument)) && !dirty;

  const onSave = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      for (const q of data.questions) {
        const next = valueOf(q.key);
        if (next !== (answers[q.key] ?? '')) await save.mutateAsync({ key: q.key, value: next });
      }
      setDraft({});
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  // Read-only: either a generation is in flight, or the spec is approved and the
  // build has moved on, so the answers can no longer change here.
  if (locked || generating) {
    return (
      <Card>
        <h2 className="mb-3 font-title text-2xl tracking-wide">Your answers</h2>
        {generating ? (
          <Alert kind="info">
            We’re generating your specification from these answers — it’ll appear on the
            Specification tab for review shortly.
          </Alert>
        ) : (
          <Alert kind="info">
            Your answers are locked now that the specification is approved and your build is under
            way. Use the Messages tab if something needs to change.
          </Alert>
        )}
        <dl className="mt-4 flex flex-col gap-5">
          {data.questions.map((q) => (
            <div key={q.key} className="border-l-2 border-primary/40 pl-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-primary/80">
                {q.prompt}
              </dt>
              <dd className="mt-1.5 whitespace-pre-wrap rounded-md bg-neutral/40 p-3 text-sm">
                {answers[q.key] || <span className="italic opacity-60">Not answered</span>}
              </dd>
            </div>
          ))}
        </dl>
        <div className="mt-4">
          <Link to="../specification" className="text-primary underline">
            Go to your specification →
          </Link>
        </div>
      </Card>
    );
  }

  const regenMissing =
    generate.error instanceof ApiError && generate.error.code === 'missing_answers'
      ? ((generate.error.details as { missing?: string[] } | undefined)?.missing ?? [])
      : null;

  return (
    <div className="flex flex-col gap-6">
      <SourceDocumentSection projectId={project.id} current={project.sourceDocument} />
      <Card>
        <form className="flex flex-col gap-4" onSubmit={(e) => void onSave(e)}>
          <div>
            <h2 className="font-title text-2xl tracking-wide">First, tell us about your product</h2>
            <p className="text-sm opacity-70">
              {hasSpec
                ? 'Update your answers any time. When you’re happy, regenerate your specification and our team will review the new version.'
                : 'Answer a few short questions about what you want to build and who it’s for — there are no wrong answers. You can edit them until you generate a specification.'}
            </p>
          </div>
          {data.questions.map((q) => (
            <TextArea
              key={q.key}
              label={`${q.prompt}${q.required ? ' *' : ''}`}
              hint={q.help}
              value={valueOf(q.key)}
              onChange={(e) => setDraft((d) => ({ ...d, [q.key]: e.target.value }))}
            />
          ))}

          {hasSpec ? (
            // A specification already exists — saving alone won't update it, so the
            // founder explicitly regenerates once their answers are how they want.
            <div className="flex flex-col gap-3 border-t border-secondary/10 pt-4">
              {regenMissing && regenMissing.length > 0 ? (
                <Alert>Answer these required questions first: {regenMissing.join(', ')}.</Alert>
              ) : generate.error instanceof ApiError &&
                generate.error.code === 'ai_not_configured' ? (
                <Alert>
                  Specification generation isn’t set up yet. Please contact your Aldemis developer.
                </Alert>
              ) : generate.isError ? (
                <Alert>Could not regenerate the specification. Please try again.</Alert>
              ) : generate.isSuccess && !dirty ? (
                <p className="text-sm text-primary">
                  Specification regenerated — our team will review the new version.
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" variant="secondary" disabled={saving || !dirty}>
                  {saving ? 'Saving…' : 'Save answers'}
                </Button>
                {saved && !dirty ? <span className="text-sm text-primary">Saved.</span> : null}
                <Button
                  type="button"
                  disabled={generate.isPending || dirty}
                  onClick={() => generate.mutate()}
                  className="ml-auto"
                >
                  {generate.isPending ? 'Regenerating…' : 'Regenerate specification'}
                </Button>
                <Link to="../specification" className="text-sm text-primary underline">
                  View specification →
                </Link>
              </div>
              {dirty ? (
                <p className="text-xs opacity-60">Save your changes, then regenerate.</p>
              ) : (
                <p className="text-xs opacity-60">
                  Regenerating replaces the current draft with a new version for our team to review.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" variant="secondary" disabled={saving || !dirty}>
                  {saving ? 'Saving…' : 'Save answers'}
                </Button>
                {saved && !dirty ? <span className="text-sm text-primary">Saved.</span> : null}
                <Link
                  to="../specification"
                  className={`${buttonClass('primary')} ml-auto ${
                    canContinue ? '' : 'pointer-events-none opacity-40'
                  }`}
                >
                  Continue to specification →
                </Link>
              </div>
              {canContinue ? null : dirty ? (
                <p className="text-xs opacity-60">Save your changes to continue.</p>
              ) : (
                <p className="text-xs opacity-60">
                  Answer the required questions (*) — or upload a document below — to continue.
                </p>
              )}
            </>
          )}
        </form>
      </Card>
    </div>
  );
}

function SourceDocumentSection({
  projectId,
  current,
}: {
  projectId: string;
  current: string | null;
}) {
  const setDoc = useSetSourceDocument(projectId);
  const [text, setText] = useState(current ?? '');

  const onFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) void file.text().then(setText);
  };

  return (
    <Card>
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h2 className="font-title text-2xl tracking-wide">Have a document already?</h2>
        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">
          Optional
        </span>
      </div>
      <p className="mb-3 text-sm opacity-70">
        Only if you want to — you can skip this entirely and just answer the questions below. If you
        already have a brief or spec, upload or paste it and we’ll use it alongside (or instead of)
        the questionnaire.
      </p>
      <input
        type="file"
        accept=".md,.markdown,.txt,text/plain,text/markdown"
        onChange={onFile}
        className="mb-3 block text-sm"
      />
      <TextArea
        label="Document"
        hint="Markdown or plain text"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="mt-3 flex items-center gap-3">
        <Button variant="secondary" disabled={setDoc.isPending} onClick={() => setDoc.mutate(text)}>
          {setDoc.isPending ? 'Saving…' : 'Save document'}
        </Button>
        {setDoc.isSuccess ? <span className="text-sm text-primary">Saved.</span> : null}
      </div>
    </Card>
  );
}
